import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Card,
    CircularProgress,
    Collapse,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { useAtom } from 'jotai';
import { useBoolean } from 'react-hanger';
import { FilterList, KeyboardArrowDown } from '@mui/icons-material';
import type { ElasticSearchFilters } from 'c-sdk';
import { ReportFieldFilterES } from 'c-main/Components/Campaign/CampaignReports/ReportFieldFilter';
import { useCommonTranslation } from 'c-translation';
import { useDateUtils } from 'c-hooks';
import { formatNumberWhole } from 'c-lib';
import useClassicPostings, {
    atom_classicPostingWindow,
    CLASSIC_POSTINGS_FILTER_FIELDS,
} from './useClassicPostings';
import PostingBreakdownCard from './PostingBreakdownCard';
import PostingLinesTable from './PostingLinesTable';
import AwaitingScreensDialog from './AwaitingScreensDialog';
import { postingStatusColor } from './PostingStatusDot';
import type { PostingSort } from './lib';
import {
    countStatuses,
    DEFAULT_POSTING_SORT,
    POSTING_WINDOW_OPTIONS,
    postingFigures,
    PostingStatus,
} from './lib';

const p = 'Modules.Main.Campaigns.ClassicPostings';

const NO_FILTERS: ElasticSearchFilters = {};

const pct = (n: number, of: number) => (of ? Math.round((n / of) * 100) : 0);

type TileProps = {
    label: string;
    value: React.ReactNode;
    sub: React.ReactNode;
    status?: PostingStatus;
    selected?: boolean;
    onClick?: () => void;
};

const Tile: React.FC<TileProps> = ({ label, value, sub, status, selected, onClick }) => (
    <Card
        variant="outlined"
        sx={theme => ({
            borderTop: status ? `3px solid ${postingStatusColor(theme, status)}` : undefined,
            borderColor: selected ? 'primary.main' : undefined,
            boxShadow: selected ? `0 0 0 1px ${theme.palette.primary.main}` : undefined,
        })}
    >
        <ButtonBase
            onClick={onClick}
            disabled={!onClick}
            sx={{ display: 'block', width: '100%', height: '100%', textAlign: 'left', p: 2 }}
        >
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h4" my={0.5} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
                {sub}
            </Typography>
        </ButtonBase>
    </Card>
);

const ClassicPostings: React.FC<{ campaignId: number }> = ({ campaignId }) => {
    const t = useCommonTranslation();
    const { formatDateString } = useDateUtils();
    const [windowDays, setWindowDays] = useAtom(atom_classicPostingWindow);

    // Report filters go to the API, like the other reports; status is derived here.
    const [fieldFilters, setFieldFilters] = useState<ElasticSearchFilters>(NO_FILTERS);
    const [status, setStatus] = useState<PostingStatus | ''>('');
    const filterPanel = useBoolean(false);
    const hasFieldFilters = Object.values(fieldFilters).some(v => v?.length);

    const whole = useClassicPostings(campaignId);
    const filtered = useClassicPostings(campaignId, hasFieldFilters, fieldFilters);
    // Keep showing the last result while a filtered request is in flight.
    const lastView = useRef(whole);
    const view = !hasFieldFilters ? whole : filtered.isLoading ? lastView.current : filtered;
    useEffect(() => {
        if (!view.isLoading) lastView.current = view;
    });
    const { lines, dataThrough, error } = view;
    const isLoading = whole.isLoading;
    const isRefreshing = hasFieldFilters && filtered.isLoading;
    const totalLines = whole.lines.length;

    const [sort, setSort] = useState<PostingSort>(DEFAULT_POSTING_SORT);
    const [awaitingOpen, setAwaitingOpen] = useState(false);

    const shown = useMemo(
        () => (status ? lines.filter(l => l.status === status) : lines),
        [lines, status],
    );
    const counts = useMemo(() => countStatuses(shown), [shown]);
    const figures = useMemo(() => postingFigures(shown), [shown]);
    // Classic impacts match expected to ~1e-10 once posted; don't show that as "−0".
    const variance = Math.round(figures.variance);

    const toggleStatus = useCallback(
        (next: PostingStatus) => setStatus(prev => (prev === next ? '' : next)),
        [],
    );
    const resetAll = useCallback(() => {
        setFieldFilters(NO_FILTERS);
        setStatus('');
    }, []);

    // Changing the window can empty a status; a filter on it would leave nothing on the page.
    useEffect(() => {
        if (status && !lines.some(l => l.status === status)) setStatus('');
    }, [lines, status]);

    const applied = useMemo(
        () => [
            ...Object.values(fieldFilters).flatMap(v => (v ?? []).map(String)),
            ...(status ? [t(`${p}.Status.${status}`)] : []),
        ],
        [fieldFilters, status, t],
    );
    const fieldFilterCount = Object.values(fieldFilters).filter(v => v?.length).length;

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{t(`${p}.loadError`)}</Alert>;
    }

    const windowLabel = (d: number) =>
        d === 0
            ? t(`${p}.bookedDate`)
            : t(d === 1 ? `${p}.windowDay` : `${p}.windowDays`, { count: d });

    const statCell = (label: string, value: React.ReactNode, sub: string, color?: string) => (
        <Box>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography
                variant="h5"
                color={color}
                sx={{ fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}
            >
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {sub}
            </Typography>
        </Box>
    );

    return (
        <Stack gap={2} pb={4}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={2}
            >
                <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="h4">{t(`${p}.title`)}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="classic-posting-window">
                            {t(`${p}.postingWindow`)}
                        </InputLabel>
                        <Select
                            labelId="classic-posting-window"
                            label={t(`${p}.postingWindow`)}
                            value={windowDays}
                            onChange={e => setWindowDays(Number(e.target.value))}
                        >
                            {POSTING_WINDOW_OPTIONS.map(d => (
                                <MenuItem key={d} value={d}>
                                    {windowLabel(d)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {dataThrough && (
                        <Typography variant="body2" color="text.secondary">
                            {t(`${p}.dataThrough`, {
                                date: formatDateString({ date: dataThrough }),
                            })}
                        </Typography>
                    )}
                    <Button
                        onClick={filterPanel.toggle}
                        color={fieldFilterCount > 0 ? 'primary' : 'secondary'}
                    >
                        <FilterList sx={{ mr: 0.5 }} />
                        {t('Modules.Main.Campaigns.Overview.Reports.filterBy')}
                        <KeyboardArrowDown />
                    </Button>
                </Stack>
            </Stack>

            <Collapse in={filterPanel.value}>
                {whole.filterValues && (
                    <ReportFieldFilterES
                        allValuesByField={whole.filterValues}
                        selectedFields={fieldFilters}
                        filterFields={CLASSIC_POSTINGS_FILTER_FIELDS}
                        showCountFieldsDropdown={false}
                        onSearch={next => setFieldFilters(next ?? NO_FILTERS)}
                        onReset={() => setFieldFilters(NO_FILTERS)}
                    />
                )}
            </Collapse>
            {isRefreshing && <LinearProgress />}

            {!lines.length ? (
                <Alert severity="info">{t(`${p}.noClassicLines`)}</Alert>
            ) : (
                <>
                    <Card variant="outlined">
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                                gap: 2,
                                p: 2,
                            }}
                        >
                            {statCell(
                                t(`${p}.Figures.delivered`),
                                formatNumberWhole(figures.delivered),
                                t(`${p}.Figures.deliveredSub`),
                            )}
                            {statCell(
                                t(`${p}.Figures.expected`),
                                formatNumberWhole(figures.expected),
                                t(`${p}.Figures.expectedSub`),
                            )}
                            {statCell(
                                t(`${p}.Figures.variance`),
                                `${variance > 0 ? '+' : variance < 0 ? '−' : ''}${formatNumberWhole(
                                    Math.abs(variance),
                                )}`,
                                t(`${p}.Figures.varianceSub`, {
                                    pct: figures.pctDelivered.toFixed(2),
                                }),
                                variance < 0 ? 'warning.main' : 'success.main',
                            )}
                            {statCell(
                                t(`${p}.Figures.unitsUp`),
                                `${pct(figures.unitsUp, figures.units)}%`,
                                t(`${p}.Figures.unitsUpSub`, {
                                    up: figures.unitsUp,
                                    total: figures.units,
                                }),
                            )}
                        </Box>
                    </Card>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                            gap: 2,
                        }}
                    >
                        <Tile
                            label={t(`${p}.Tiles.postersUp`)}
                            value={
                                <>
                                    {figures.unitsUp}{' '}
                                    <Typography component="span" color="text.secondary">
                                        {t(`${p}.Tiles.postersUpOf`, { total: figures.units })}
                                    </Typography>
                                </>
                            }
                            sub={t(`${p}.Tiles.postersUpSub`)}
                        />
                        <Tile
                            status={PostingStatus.Within}
                            label={t(`${p}.Status.in`)}
                            value={counts.in}
                            sub={t(`${p}.Tiles.pctOfUnits`, { pct: pct(counts.in, shown.length) })}
                            selected={status === PostingStatus.Within}
                            onClick={() => toggleStatus(PostingStatus.Within)}
                        />
                        <Tile
                            status={PostingStatus.Out}
                            label={t(`${p}.Status.out`)}
                            value={counts.out}
                            sub={t(
                                windowDays === 1
                                    ? `${p}.Tiles.outOfWindowSubOne`
                                    : `${p}.Tiles.outOfWindowSub`,
                                { count: windowDays },
                            )}
                            selected={status === PostingStatus.Out}
                            onClick={() => toggleStatus(PostingStatus.Out)}
                        />
                        <Tile
                            status={PostingStatus.Awaiting}
                            label={t(`${p}.Status.wait`)}
                            value={counts.wait}
                            sub={
                                <>
                                    {t(`${p}.Tiles.pctOfUnits`, {
                                        pct: pct(counts.wait, shown.length),
                                    })}
                                    {counts.wait > 0 && (
                                        <Typography
                                            component="div"
                                            variant="caption"
                                            color="primary"
                                            fontWeight={600}
                                        >
                                            {t(`${p}.Tiles.viewScreens`)}
                                        </Typography>
                                    )}
                                </>
                            }
                            onClick={counts.wait > 0 ? () => setAwaitingOpen(true) : undefined}
                        />
                    </Box>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        gap={1}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {applied.length
                                ? `${t(`${p}.Filters.showing`, {
                                      shown: shown.length,
                                      total: totalLines,
                                  })} — ${applied.join(' · ')}`
                                : t(`${p}.Filters.allLines`, { count: totalLines })}
                        </Typography>
                        {applied.length > 0 && (
                            <Button size="small" variant="text" onClick={resetAll}>
                                {t(`${p}.Filters.reset`)}
                            </Button>
                        )}
                    </Stack>

                    {shown.length > 0 && (
                        <>
                            <PostingBreakdownCard
                                title={t(`${p}.Breakdown.byOwner`)}
                                lines={shown}
                                groupBy="owner"
                            />
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                                    gap: 2,
                                }}
                            >
                                <PostingBreakdownCard
                                    title={t(`${p}.Breakdown.byState`)}
                                    lines={shown}
                                    groupBy="state"
                                />
                                <PostingBreakdownCard
                                    title={t(`${p}.Breakdown.byMarket`)}
                                    lines={shown}
                                    groupBy="market"
                                />
                            </Box>
                        </>
                    )}

                    <PostingLinesTable lines={shown} sort={sort} onSortChange={setSort} />
                </>
            )}

            <AwaitingScreensDialog
                open={awaitingOpen}
                onClose={() => setAwaitingOpen(false)}
                onShowInLines={() => {
                    setAwaitingOpen(false);
                    setStatus(PostingStatus.Awaiting);
                }}
                lines={lines}
                dataThrough={dataThrough}
            />
        </Stack>
    );
};

export default ClassicPostings;
